/**
 * macOS rounded corners for Tauri frameless windows.
 * Mirrors @cloudworxx/tauri-plugin-mac-rounded-corners (plain JS for CRA/webpack).
 */
import { invoke } from '@tauri-apps/api/core';
import { getCurrentWebviewWindow } from '@tauri-apps/api/webviewWindow';
import { makeSafeUnlisten } from '../utils/safeTauriEvent';

/** @type {{ cornerRadius?: number; offsetX?: number; offsetY?: number } | null} */
let currentConfig = null;

/** @type {(() => void) | null} */
let unlistenResize = null;

/** @type {Promise<void>} serializes concurrent setup calls (StrictMode/HMR double-mount) */
let setupChain = Promise.resolve();

export async function repositionTrafficLights() {
  if (!currentConfig) return;
  try {
    const window = getCurrentWebviewWindow();
    await invoke('reposition_traffic_lights', {
      window,
      offsetX: currentConfig.offsetX ?? 0,
      offsetY: currentConfig.offsetY ?? 0,
    });
  } catch (error) {
    console.error('Failed to reposition traffic lights:', error);
  }
}

async function setupResizeListener() {
  // Serialized + null-before-await: the old code called `unlistenResize()`
  // without clearing it and left the stale handle set during the await, so a
  // cleanup firing in that window (StrictMode/HMR remount) called the
  // already-unregistered listener again — the "listeners[eventId].handlerId"
  // uncaught runtime error on app open.
  setupChain = setupChain.then(async () => {
    if (unlistenResize) {
      const stale = unlistenResize;
      unlistenResize = null;
      stale();
    }
    const window = getCurrentWebviewWindow();
    try {
      unlistenResize = makeSafeUnlisten(
        await window.onResized(() => {
          repositionTrafficLights();
        })
      );
      if (!currentConfig) {
        // cleaned up while we were registering — tear the new listener down
        const fn = unlistenResize;
        unlistenResize = null;
        fn();
      }
    } catch (error) {
      console.error('Failed to setup resize listener:', error);
    }
  });
  return setupChain;
}

/**
 * @param {{ cornerRadius?: number; offsetX?: number; offsetY?: number }} [config]
 */
export async function enableRoundedCorners(config) {
  currentConfig = config || {};
  const window = getCurrentWebviewWindow();
  await invoke('enable_rounded_corners', {
    window,
    offsetX: config?.offsetX ?? 0,
    offsetY: config?.offsetY ?? 0,
  });
  await setupResizeListener();
}

/**
 * @param {{ cornerRadius?: number; offsetX?: number; offsetY?: number }} [config]
 */
export async function enableModernWindowStyle(config) {
  currentConfig = config || {};
  const window = getCurrentWebviewWindow();
  await invoke('enable_modern_window_style', {
    window,
    cornerRadius: config?.cornerRadius ?? 12,
    offsetX: config?.offsetX ?? 0,
    offsetY: config?.offsetY ?? 0,
  });
  await setupResizeListener();
}

export function cleanupRoundedCorners() {
  if (unlistenResize) {
    unlistenResize();
    unlistenResize = null;
  }
  currentConfig = null;
}
